import {useEffect, useMemo, useState} from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Drawer,
    FormControlLabel,
    Stack,
    TextField,
    Typography,
    Divider,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';
import {
    buildColumnKey,
    transferGlInvoiceCategory,
    transferGlInvoiceCategoryBatch,
    type GlColumnDef,
    type GlInvoiceTransferHistoryVO,
} from '@/services/accountService/glInvoiceService';
import {
    fetchTransferDestinationCategories,
    type GlCategory,
} from '@/services/accountService/glCategoryService';
import {listAll as listAllFsCategories, type FsCategory} from '@/services/accountService/fsCategoryService';
import {listAccountsByCompany, type GlAccount} from '@/services/accountService/companyGLCategoryService';
import {isExRateColumn, stripDupSuffix} from '@/components/account/gl-invoice/mode/tabs/bankColumnUtils';
import type {GlTransferInvoicePayload} from '@/components/account/gl-invoice/mode/useGlInvoiceTransferDrawer';
import {allocateTransferAmounts} from '@/components/account/gl-invoice/mode/glTransferAlloc';
import {useShowClientAlias} from '@/pages/components/account/gl-invoice/useShowClientAlias';

export interface GlTransferDestination {
    fsCode: string;
    glCode: string;
    nameEn: string;
    nameZh?: string;
    aliasEn?: string;
    aliasZh?: string;
    columnKey: string;
    label: string;
}

function destinationDisplayLabel(d: GlTransferDestination, showAlias: boolean): string {
    const base = d.nameZh ? `${d.nameEn} (${d.nameZh})` : d.nameEn;
    if (!showAlias) return base;
    const client = (d.aliasEn || d.aliasZh || '').trim();
    if (!client || client.toLowerCase() === d.nameEn.toLowerCase()) return base;
    return `${base} · ${client}`;
}

function enrichDestinationsWithAliases(
    list: GlTransferDestination[],
    coGl: GlAccount[],
): GlTransferDestination[] {
    const byCode = new Map<string, GlAccount>();
    for (const a of coGl) {
        if (!a?.code || a.isDel === 1 || a.isActive === 0) continue;
        byCode.set(a.code.trim(), a);
    }
    return list.map((d) => {
        const alias =
            byCode.get(d.glCode)
            ?? byCode.get(stripDupSuffix(d.glCode))
            ?? byCode.get(d.fsCode);
        const aliasEn = (alias?.aliasEn || '').trim() || undefined;
        const aliasZh = (alias?.aliasZh || '').trim() || undefined;
        return {...d, aliasEn, aliasZh};
    });
}

/** 与后端 isCreditFs 对齐：Cr 科目（负债/权益/收入） */
export function isCreditFsCode(
    fsCode: string | null | undefined,
    entryByFs?: Map<string, string>,
): boolean {
    if (!fsCode) return false;
    const entry = entryByFs?.get(fsCode)?.trim();
    if (entry) {
        if (/^cr/i.test(entry) || /^credit/i.test(entry)) return true;
        if (/^dr/i.test(entry) || /^debit/i.test(entry)) return false;
    }
    const upper = fsCode.trim().toUpperCase();
    return upper.startsWith('BS-CL-')
        || upper.startsWith('BS-EQ-')
        || upper.startsWith('BS-NCL-')
        || upper.startsWith('PL-TURN-')
        || upper.startsWith('PL-OI-')
        || upper.startsWith('PL-FIN-')
        || upper.startsWith('R-');
}

function buildFsEntryMap(fsList: FsCategory[]): Map<string, string> {
    const map = new Map<string, string>();
    const walk = (nodes: FsCategory[]) => {
        for (const n of nodes) {
            if (n.code && n.entry) map.set(n.code, n.entry);
            if (n.children?.length) walk(n.children);
        }
    };
    walk(fsList);
    return map;
}

function formatAmount(v: number | null | undefined) {
    if (v == null) return '—';
    const num = Number(v);
    if (Number.isNaN(num)) return '—';
    if (num < 0) {
        return `(${Math.abs(num).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})})`;
    }
    return num.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
}

function formatTime(v?: string | null) {
    if (!v) return '—';
    try {
        return new Date(v).toLocaleString();
    } catch {
        return String(v);
    }
}

function fromCategoryLabel(
    details: GlInvoiceTransferHistoryVO['beforeDetails'] | undefined,
    destinations: GlTransferDestination[],
): string {
    if (!details || details.length === 0) return '—';
    const byKey = new Map(destinations.map((d) => [d.columnKey, d.nameEn]));
    const byFs = new Map<string, string>();
    for (const d of destinations) {
        if (!byFs.has(d.fsCode)) byFs.set(d.fsCode, d.nameEn);
    }
    const fallback: Record<string, string> = {
        'BS-CA-PND': 'Paid no docs',
        'BS-CA-PPM': 'Prepayment',
        'PL-COS-PUR': 'Purchase',
        'BS-EQ-IC': 'Share capital',
        'BS-CL-AP': 'Account payable',
    };
    const meaningful = details.filter((d) => d.fsCode && d.fsCode !== 'BS-CA-CASH');
    const use = meaningful.length > 0 ? meaningful : details;
    const fsCodes = [...new Set(use.map((d) => d.fsCode!).filter(Boolean))];
    return fsCodes.map((fs) => {
        const sample = use.find((d) => d.fsCode === fs);
        const key = `${fs}|${sample?.glCode || ''}`;
        return byKey.get(key) || byFs.get(fs) || fallback[fs] || fs;
    }).join(', ');
}

function toCategoryLabel(
    h: GlInvoiceTransferHistoryVO,
    destinations: GlTransferDestination[],
): string {
    const afterNonBank = h.afterDetails?.find((d) => d.fsCode && d.fsCode !== 'BS-CA-CASH');
    const fs = afterNonBank?.fsCode || h.targetFsCode || h.afterDetails?.[0]?.fsCode || '';
    const gl = afterNonBank?.glCode || h.targetGlCode || h.afterDetails?.[0]?.glCode || '';
    if (!fs) return '—';
    const hit = destinations.find((d) => d.fsCode === fs && (d.glCode || '') === (gl || ''));
    if (hit) return hit.nameEn;
    const byFs = destinations.find((d) => d.fsCode === fs);
    if (byFs) return byFs.nameEn;
    const fallback: Record<string, string> = {
        'BS-CA-PND': 'Paid no docs',
        'BS-CA-PPM': 'Prepayment',
        'PL-COS-PUR': 'Purchase',
        'BS-EQ-IC': 'Share capital',
        'BS-CL-AP': 'Account payable',
    };
    return fallback[fs] || `${fs}|${gl}`;
}

/** 由当前 Sheet columnDefs 构建（兜底 / 历史标签补充） */
export function buildTransferDestinations(columnDefs: GlColumnDef[]): GlTransferDestination[] {
    const seen = new Set<string>();
    const list: GlTransferDestination[] = [];
    for (const def of columnDefs) {
        // 银行列（原币/Ex HKD）不作为转移目标；转移只改分类列，银行行由后端原样保留
        if (isExRateColumn(def) || def.fsCode === 'BS-CA-CASH') continue;
        const columnKey = buildColumnKey(def);
        if (seen.has(columnKey)) continue;
        seen.add(columnKey);
        list.push({
            fsCode: def.fsCode,
            glCode: def.glCode || '',
            nameEn: def.nameEn,
            nameZh: def.nameZh,
            aliasEn: def.aliasEn,
            aliasZh: def.aliasZh,
            columnKey,
            label: def.nameZh ? `${def.nameEn} (${def.nameZh})` : def.nameEn,
        });
    }
    return list;
}

/** 由 account_gl_category 全量叶子构建转移目标 */
export function buildTransferDestinationsFromCategories(
    categories: GlCategory[],
): GlTransferDestination[] {
    const seen = new Set<string>();
    const list: GlTransferDestination[] = [];
    for (const cat of categories) {
        if (!cat.fsCode || !cat.code || cat.fsCode === 'BS-CA-CASH') continue;
        const columnKey = buildColumnKey({fsCode: cat.fsCode, glCode: cat.code});
        if (seen.has(columnKey)) continue;
        seen.add(columnKey);
        const nameEn = cat.nameEn || cat.code;
        list.push({
            fsCode: cat.fsCode,
            glCode: cat.code,
            nameEn,
            nameZh: cat.nameZh,
            columnKey,
            label: cat.nameZh ? `${nameEn} (${cat.nameZh})` : nameEn,
        });
    }
    list.sort((a, b) => a.nameEn.localeCompare(b.nameEn, undefined, {sensitivity: 'base'}));
    return list;
}

interface GlInvoiceTransferDrawerProps {
    open: boolean;
    onClose: () => void;
    companyId: number;
    annualId: number;
    /** 单票或多票 */
    invoices: GlTransferInvoicePayload[];
    /** @deprecated 兼容；优先用 invoices */
    invoice?: GlTransferInvoicePayload | null;
    /** 可选兜底（Sheet columnDefs）；优先用全量 GL 字典 */
    destinations?: GlTransferDestination[];
    history: GlInvoiceTransferHistoryVO[];
    historyLoading?: boolean;
    onTransferSuccess?: () => void | Promise<void>;
    onHistoryRefresh?: (invoiceId: string) => void | Promise<void>;
}

export default function GlInvoiceTransferDrawer(props: GlInvoiceTransferDrawerProps) {
    const {
        open,
        onClose,
        companyId,
        annualId,
        destinations: fallbackDestinations = [],
        history,
        historyLoading,
        onTransferSuccess,
        onHistoryRefresh,
    } = props;

    const invoices = useMemo(() => {
        if (props.invoices?.length) return props.invoices;
        return props.invoice ? [props.invoice] : [];
    }, [props.invoices, props.invoice]);

    const primary = invoices[0] ?? null;
    const isMulti = invoices.length > 1;

    const [selected, setSelected] = useState<GlTransferDestination | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [fullDestinations, setFullDestinations] = useState<GlTransferDestination[]>([]);
    const [fsEntryByCode, setFsEntryByCode] = useState<Map<string, string>>(() => new Map());
    const [destLoading, setDestLoading] = useState(false);
    /** 用户输入的转移金额（绝对值正数） */
    const [transferAbs, setTransferAbs] = useState<string>('');
    const {showClientAlias, setShowClientAlias} = useShowClientAlias();

    const sourceAbs = useMemo(() => {
        return invoices.reduce((sum, inv) => {
            const a = inv.amount;
            if (a == null || Number.isNaN(Number(a))) return sum;
            return sum + Math.abs(Number(a));
        }, 0);
    }, [invoices]);

    const sourceIsCredit = useMemo(
        () => isCreditFsCode(primary?.sourceFsCode, fsEntryByCode),
        [primary?.sourceFsCode, fsEntryByCode],
    );

    const invoiceKey = useMemo(
        () => invoices.map((i) => `${i.invoiceId}:${i.amount}`).join('|'),
        [invoices],
    );

    useEffect(() => {
        setSelected(null);
        setError(null);
        if (sourceAbs > 0) {
            setTransferAbs(sourceAbs.toFixed(2));
        } else {
            setTransferAbs('');
        }
    }, [invoiceKey, open, sourceAbs]);

    // 打开抽屉时拉取全量 GL 列 + Co. GL 别名 + FS entry（用于同向 Dr/Cr 过滤）
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setDestLoading(true);
        Promise.all([
            fetchTransferDestinationCategories(),
            listAllFsCategories().catch((e) => {
                console.warn('[GlInvoiceTransferDrawer] FS list failed, using prefix fallback:', e);
                return [] as FsCategory[];
            }),
            companyId
                ? listAccountsByCompany(companyId).catch(() => [] as GlAccount[])
                : Promise.resolve([] as GlAccount[]),
        ])
            .then(([cats, fsList, coGl]) => {
                if (cancelled) return;
                setFsEntryByCode(buildFsEntryMap(Array.isArray(fsList) ? fsList : []));
                const base = buildTransferDestinationsFromCategories(cats);
                setFullDestinations(enrichDestinationsWithAliases(base, coGl ?? []));
            })
            .catch((e) => {
                console.error('[GlInvoiceTransferDrawer] Failed to load GL destinations:', e);
                if (!cancelled) {
                    setFullDestinations([]);
                    setFsEntryByCode(new Map());
                }
            })
            .finally(() => {
                if (!cancelled) setDestLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, companyId]);

    const options = useMemo(() => {
        const base = fullDestinations.length > 0 ? fullDestinations : fallbackDestinations;
        const srcFs = primary?.sourceFsCode;
        const srcGl = primary?.sourceGlCode || '';
        return base.filter((d) => {
            // 排除自身
            if (srcFs && d.fsCode === srcFs && (d.glCode || '') === srcGl) return false;
            // 同源 Dr/Cr
            if (srcFs) {
                const destCr = isCreditFsCode(d.fsCode, fsEntryByCode);
                if (destCr !== sourceIsCredit) return false;
            }
            return true;
        });
    }, [
        fullDestinations,
        fallbackDestinations,
        primary?.sourceFsCode,
        primary?.sourceGlCode,
        fsEntryByCode,
        sourceIsCredit,
    ]);

    // 已选目标若不在过滤后列表中则清空
    useEffect(() => {
        if (!selected) return;
        if (!options.some((o) => o.columnKey === selected.columnKey)) {
            setSelected(null);
        }
    }, [options, selected]);

    const parsedTransferAbs = useMemo(() => {
        const n = Number(transferAbs);
        return Number.isFinite(n) ? n : NaN;
    }, [transferAbs]);

    const allocation = useMemo(() => {
        if (!Number.isFinite(parsedTransferAbs) || parsedTransferAbs <= 0) return [];
        return allocateTransferAmounts(
            invoices.map((inv) => ({
                invoiceId: inv.invoiceId,
                amount: Number(inv.amount) || 0,
            })),
            parsedTransferAbs,
        );
    }, [invoices, parsedTransferAbs]);

    const remainderAbs = useMemo(() => {
        if (!Number.isFinite(parsedTransferAbs) || sourceAbs <= 0) return null;
        return Math.max(0, Math.round((sourceAbs - parsedTransferAbs) * 100) / 100);
    }, [parsedTransferAbs, sourceAbs]);

    const amountValid =
        Number.isFinite(parsedTransferAbs)
        && parsedTransferAbs > 0
        && (sourceAbs <= 0 || parsedTransferAbs <= sourceAbs + 1e-9);

    const canConfirm = invoices.length > 0 && !!selected && !submitting && !destLoading && amountValid;

    const handleClose = () => {
        setSelected(null);
        setError(null);
        setTransferAbs('');
        onClose();
    };

    const handleConfirm = async () => {
        if (!primary || !selected || !amountValid) return;
        const items = allocation.filter((a) => a.transferAbs > 1e-9);
        if (items.length === 0) return;
        setSubmitting(true);
        setError(null);
        try {
            if (items.length === 1 && !isMulti) {
                await transferGlInvoiceCategory({
                    companyId,
                    annualId,
                    invoiceId: items[0].invoiceId,
                    targetFsCode: selected.fsCode,
                    targetGlCode: selected.glCode || null,
                    sourceFsCode: primary.sourceFsCode || null,
                    sourceGlCode: primary.sourceGlCode || null,
                    transferAmount: items[0].transferAbs,
                });
                await onHistoryRefresh?.(items[0].invoiceId);
            } else {
                await transferGlInvoiceCategoryBatch({
                    companyId,
                    annualId,
                    targetFsCode: selected.fsCode,
                    targetGlCode: selected.glCode || null,
                    sourceFsCode: primary.sourceFsCode || null,
                    sourceGlCode: primary.sourceGlCode || null,
                    items: items.map((a) => ({
                        invoiceId: a.invoiceId,
                        transferAmount: a.transferAbs,
                    })),
                });
            }
            setSelected(null);
            await onTransferSuccess?.();
            handleClose();
        } catch (e: any) {
            setError(e?.message || 'Transfer failed');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    width: {xs: '100%', sm: 480},
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
            sx={{zIndex: (theme) => theme.zIndex.modal + 10}}
        >
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1.5,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            }}>
                <Typography variant="h6" sx={{fontWeight: 600}}>
                    {isMulti ? `Transfer Invoices (${invoices.length})` : 'Transfer Invoice'}
                </Typography>
                <Button onClick={handleClose}>Close</Button>
            </Box>

            <Box sx={{flex: 1, overflow: 'auto', px: 2, py: 2}}>
                {primary && (
                    <Stack spacing={1.5} mb={2}>
                        <Typography variant="subtitle2" color="text.secondary">
                            {isMulti ? 'Selected invoices' : 'Invoice'}
                        </Typography>
                        {!isMulti ? (
                            <Box sx={{
                                p: 1.5,
                                borderRadius: 1,
                                bgcolor: 'action.hover',
                            }}>
                                <Typography variant="body2">
                                    <strong>Date:</strong> {primary.date || '—'}
                                </Typography>
                                <Typography variant="body2" sx={{mt: 0.5}}>
                                    <strong>Description:</strong> {primary.description || '—'}
                                </Typography>
                                <Typography variant="body2" sx={{mt: 0.5}}>
                                    <strong>Amount:</strong> {formatAmount(primary.amount)}
                                </Typography>
                                {(primary.sourceLabel || primary.sourceFsCode) && (
                                    <Typography variant="body2" sx={{mt: 0.5}}>
                                        <strong>From:</strong>{' '}
                                        {primary.sourceLabel || primary.sourceFsCode}
                                        {primary.sourceGlCode ? ` (${primary.sourceFsCode}|${primary.sourceGlCode})` : ''}
                                    </Typography>
                                )}
                            </Box>
                        ) : (
                            <Box sx={{
                                p: 1.5,
                                borderRadius: 1,
                                bgcolor: 'action.hover',
                                maxHeight: 220,
                                overflow: 'auto',
                            }}>
                                {(primary.sourceLabel || primary.sourceFsCode) && (
                                    <Typography variant="body2" sx={{mb: 1}}>
                                        <strong>From:</strong>{' '}
                                        {primary.sourceLabel || primary.sourceFsCode}
                                        {primary.sourceGlCode ? ` (${primary.sourceFsCode}|${primary.sourceGlCode})` : ''}
                                    </Typography>
                                )}
                                {allocation.map((a, idx) => {
                                    const inv = invoices[idx];
                                    return (
                                        <Box
                                            key={a.invoiceId}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                gap: 1,
                                                py: 0.5,
                                                borderTop: idx > 0 ? (theme) => `1px solid ${theme.palette.divider}` : undefined,
                                            }}
                                        >
                                            <Typography variant="caption" sx={{flex: 1, minWidth: 0}} noWrap>
                                                {inv?.description || a.invoiceId}
                                                {a.split ? ' · Split' : ''}
                                            </Typography>
                                            <Typography variant="caption" sx={{fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap'}}>
                                                {formatAmount(inv?.amount)} → {a.transferAbs.toFixed(2)}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}

                        <Typography variant="subtitle2" color="text.secondary" sx={{pt: 1}}>
                            Transfer amount
                        </Typography>
                        <TextField
                            size="small"
                            type="number"
                            fullWidth
                            value={transferAbs}
                            onChange={(e) => setTransferAbs(e.target.value)}
                            inputProps={{min: 0, step: '0.01'}}
                            placeholder="Amount to move"
                            helperText={
                                sourceAbs > 0
                                    ? `Max ${sourceAbs.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
                                      + (remainderAbs != null && Number.isFinite(parsedTransferAbs)
                                          ? ` · Remainder ${remainderAbs.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} stays in source`
                                          : '')
                                      + (isMulti && allocation.some((a) => a.split) ? ' · Last row will be split' : '')
                                    : 'Enter amount to transfer'
                            }
                            error={transferAbs !== '' && !amountValid}
                        />

                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{pt: 1}}>
                            <Typography variant="subtitle2" color="text.secondary">
                                Transfer to
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={showClientAlias}
                                        onChange={(e) => setShowClientAlias(e.target.checked)}
                                    />
                                }
                                label={<Typography variant="caption">Show client names</Typography>}
                                sx={{mr: 0, ml: 0}}
                            />
                        </Stack>
                        <Autocomplete
                            options={options}
                            value={selected}
                            onChange={(_, v) => setSelected(v)}
                            getOptionLabel={(opt) => destinationDisplayLabel(opt, showClientAlias)}
                            isOptionEqualToValue={(a, b) => a.columnKey === b.columnKey}
                            filterOptions={(opts, state) => {
                                const q = state.inputValue.trim().toLowerCase();
                                if (!q) return opts;
                                return opts.filter((o) => {
                                    const hay = [
                                        o.nameEn, o.nameZh, o.aliasEn, o.aliasZh, o.fsCode, o.glCode, o.label,
                                    ].filter(Boolean).join(' ').toLowerCase();
                                    return hay.includes(q);
                                });
                            }}
                            openOnFocus
                            fullWidth
                            loading={destLoading}
                            disabled={destLoading && options.length === 0}
                            noOptionsText={
                                destLoading
                                    ? 'Loading…'
                                    : options.length === 0
                                        ? 'No GL columns available'
                                        : 'No match'
                            }
                            slotProps={{
                                popper: {sx: {zIndex: (theme) => theme.zIndex.modal + 20}},
                            }}
                            ListboxProps={{style: {maxHeight: 320}}}
                            renderOption={(liProps, opt) => (
                                <li {...liProps} key={opt.columnKey}>
                                    <Box sx={{display: 'flex', flexDirection: 'column'}}>
                                        <Typography variant="body2">{opt.nameEn}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {opt.columnKey}
                                        </Typography>
                                    </Box>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    placeholder={destLoading ? 'Loading GL columns…' : 'Search GL column…'}
                                    helperText={
                                        !destLoading && options.length === 0
                                            ? (primary?.sourceFsCode
                                                ? `No ${sourceIsCredit ? 'Cr' : 'Dr'} destinations available`
                                                : 'No destination columns loaded')
                                            : !destLoading && options.length > 0
                                                ? `${options.length} ${sourceIsCredit ? 'Cr' : 'Dr'} columns`
                                                  + (primary?.sourceFsCode ? ' (same side as source)' : '')
                                                : undefined
                                    }
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {destLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />

                        {error && (
                            <Typography variant="caption" color="error">{error}</Typography>
                        )}

                        <Button
                            variant="contained"
                            disabled={!canConfirm}
                            onClick={handleConfirm}
                            startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : undefined}
                            sx={{textTransform: 'none', alignSelf: 'flex-start'}}
                        >
                            Confirm Transfer
                        </Button>
                    </Stack>
                )}

                {!isMulti && (
                    <>
                        <Divider sx={{my: 1}} />

                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Transfer History
                        </Typography>
                        {historyLoading ? (
                            <Box sx={{py: 2, textAlign: 'center'}}>
                                <CircularProgress size={22} />
                            </Box>
                        ) : history.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{py: 1}}>
                                No transfer records yet.
                            </Typography>
                        ) : (
                            <List dense disablePadding>
                                {history.map((h) => {
                                    const from = fromCategoryLabel(h.beforeDetails, options);
                                    const to = toCategoryLabel(h, options);
                                    return (
                                    <ListItem key={h.batchId} alignItems="flex-start" sx={{px: 0}}>
                                        <ListItemText
                                            primary={`${from} → ${to}`}
                                            secondary={`${formatTime(h.changeTime)}${h.changeUserId != null ? ` · user ${h.changeUserId}` : ''}`}
                                            primaryTypographyProps={{variant: 'body2', fontWeight: 600}}
                                            secondaryTypographyProps={{variant: 'caption'}}
                                        />
                                    </ListItem>
                                    );
                                })}
                            </List>
                        )}
                    </>
                )}
            </Box>
        </Drawer>
    );
}

